/**
 * Vidhi Farm colour tokens.
 *
 * Every value here is checked against the surfaces it is actually used on, and
 * the check runs in CI (scripts/check-contrast.js). The site shipped with
 * `text.secondary: #B7AA9E`, which is **2.01:1** on the page background — body
 * copy that most people simply cannot read. Numbers below are WCAG contrast
 * ratios; AA wants 4.5 for body text and 3.0 for large or bold text.
 *
 * The rule that keeps biting: MUSTARD IS NOT A TEXT COLOUR ON LIGHT SURFACES.
 * `#D8B510` is 1.76:1 on the page. It is a surface you put dark text on, or a
 * text colour on the dark brown panel (5.38:1). Never `color: mustard` on cream.
 */

export const surface = {
  page: '#F2F1EC',   // soft off-white
  card: '#FFFFFF',
  sunken: '#EAE7DF',
  dark: '#4B3A37',   // deep earth brown panel
  darker: '#2D201E',
};

export const brand = {
  brown: '#4B3A37',
  brownDark: '#2D201E',
  mustard: '#D8B510',   // surfaces and dark-panel text only
  mustardInk: '#7F6412', // the readable mustard, for text on light  (4.97 / 5.62)
  olive: '#8A9B4F',
  oliveInk: '#5C6B2F',   // readable olive for text on light
  clay: '#C25B3F',
};

export const ink = {
  primary: '#4B3A37',    // 9.48 on page, 10.72 on card
  secondary: '#6B5A52',  // 5.79 / 6.54 — replaces #B7AA9E (2.01)
  muted: '#7A6A62',      // 4.57 / 5.17 — the lightest text we allow
  onDark: '#F2F1EC',     // 9.48 on the brown panel
  onDarkMuted: '#CFC6BA',// 6.35 on the brown panel
  onMustard: '#2A1109',  // dark text for mustard buttons
};

/** Display face for headings. Body stays Inter. */
export const type = {
  display: '"Fraunces", "Georgia", serif',
  body: '"Inter", system-ui, sans-serif',
};

export const line = {
  subtle: 'rgba(75,58,55,0.10)',
  default: 'rgba(75,58,55,0.18)',
  strong: 'rgba(75,58,55,0.32)',
};

export const status = {
  success: '#3D6B33',
  warning: '#8A5A08',
  error: '#A32E22',
  info: '#255F87',
};

/**
 * Pairs the CI check asserts. Adding a text colour without adding it here means
 * it is unchecked, so the check also fails on tokens it does not know about.
 */
export const CONTRAST_CONTRACT = [
  { name: 'ink.primary on page', fg: ink.primary, bg: surface.page, min: 4.5 },
  { name: 'ink.primary on card', fg: ink.primary, bg: surface.card, min: 4.5 },
  { name: 'ink.secondary on page', fg: ink.secondary, bg: surface.page, min: 4.5 },
  { name: 'ink.secondary on card', fg: ink.secondary, bg: surface.card, min: 4.5 },
  { name: 'ink.muted on page', fg: ink.muted, bg: surface.page, min: 4.5 },
  { name: 'ink.muted on card', fg: ink.muted, bg: surface.card, min: 4.5 },
  { name: 'brand.mustardInk on page', fg: brand.mustardInk, bg: surface.page, min: 4.5 },
  { name: 'brand.mustardInk on card', fg: brand.mustardInk, bg: surface.card, min: 4.5 },
  { name: 'brand.oliveInk on page', fg: brand.oliveInk, bg: surface.page, min: 4.5 },
  { name: 'ink.onDark on dark panel', fg: ink.onDark, bg: surface.dark, min: 4.5 },
  { name: 'ink.onDarkMuted on dark panel', fg: ink.onDarkMuted, bg: surface.dark, min: 4.5 },
  { name: 'ink.onMustard on mustard', fg: ink.onMustard, bg: brand.mustard, min: 4.5 },
  { name: 'brand.mustard on dark panel', fg: brand.mustard, bg: surface.dark, min: 4.5 },
  { name: 'status.success on card', fg: status.success, bg: surface.card, min: 4.5 },
  { name: 'status.warning on card', fg: status.warning, bg: surface.card, min: 4.5 },
  { name: 'status.error on card', fg: status.error, bg: surface.card, min: 4.5 },
  { name: 'status.info on card', fg: status.info, bg: surface.card, min: 4.5 },
];

/** Colours that must never be used as text on a light surface. */
export const NEVER_TEXT_ON_LIGHT = [brand.mustard, brand.olive, '#B7AA9E', '#7f8c8d'];

const tokens = { surface, brand, ink, line, status };
export default tokens;
